import { useEffect, useState } from "react";
import { clearResponse, fetchExport, loadQueue, loadSources, setResponse } from "./api";
import { FeedbackModule, type QueueState } from "./FeedbackModule";
import { feedbackItemKey, type FeedbackItem, type FeedbackSource } from "./types";

export default function FeedbackPage() {
  const [sources, setSources] = useState<FeedbackSource[]>([]);
  const [selectedSourceId, setSelectedSourceId] = useState("");
  const [queueState, setQueueState] = useState<QueueState>({ status: "loading" });
  const [savingItemIds, setSavingItemIds] = useState<Set<string>>(new Set());
  const [actionError, setActionError] = useState<string>();
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let active = true;
    void loadSources().then((loaded) => {
      if (!active) return;
      setSources(loaded);
      setSelectedSourceId((current) => current || loaded[0]?.source_id || "");
      if (loaded.length === 0) setQueueState({ status: "ready", queue: { source_id: "", items: [] } });
    }).catch((error: unknown) => {
      if (active) setQueueState({ status: "unavailable", message: error instanceof Error ? error.message : "Feedback sources unavailable" });
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!selectedSourceId) return undefined;
    let active = true;
    setQueueState({ status: "loading" });
    void loadQueue(selectedSourceId).then((queue) => {
      if (active) setQueueState({ status: "ready", queue });
    }).catch((error: unknown) => {
      if (active) setQueueState({ status: "unavailable", message: error instanceof Error ? error.message : "Feedback source unavailable" });
    });
    return () => { active = false; };
  }, [selectedSourceId, retryCount]);

  const mutate = async (item: FeedbackItem, apply: () => Promise<FeedbackItem>) => {
    setActionError(undefined);
    const operationKey = feedbackItemKey(item);
    setSavingItemIds((current) => new Set(current).add(operationKey));
    try {
      const updated = await apply();
      setQueueState((current) => current.status === "ready" && current.queue.source_id === item.source_id ? {
        status: "ready",
        queue: { ...current.queue, items: current.queue.items.map((candidate) => candidate.source_id === item.source_id && candidate.item_id === item.item_id ? updated : candidate) },
      } : current);
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Could not save feedback");
    } finally {
      setSavingItemIds((current) => {
        const next = new Set(current);
        next.delete(operationKey);
        return next;
      });
    }
  };

  const save = (item: FeedbackItem, value: string | null, reason?: string) =>
    mutate(item, () => setResponse(item.source_id, item.item_id, value, reason));

  const clear = (item: FeedbackItem) =>
    mutate(item, async () => {
      await clearResponse(item.source_id, item.item_id);
      return { ...item, current_response: undefined, current_reason: undefined };
    });

  const download = async () => {
    try {
      const { blob, filename } = await fetchExport(selectedSourceId);
      const href = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = href;
      anchor.download = filename;
      anchor.click();
      URL.revokeObjectURL(href);
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Could not export feedback");
    }
  };

  return (
    <FeedbackModule
      sources={sources}
      selectedSourceId={selectedSourceId}
      queueState={queueState}
      savingItemIds={savingItemIds}
      actionError={actionError}
      onSelectSource={setSelectedSourceId}
      // Changing a label carries the existing note forward rather than dropping
      // it: the note is the expensive part to retype, and it can be edited or
      // emptied in place once the new label is chosen.
      onRespond={(item, value) => { void save(item, value, item.current_reason); }}
      onClear={(item) => { void clear(item); }}
      // A note saves against whatever verdict the row holds, including none: a
      // heading takes no verdict, and an item can be explained before it is
      // rated. Emptying the last of both leaves nothing to store, which the
      // provider records by deleting the row's document.
      onReason={(item, reason) => { void save(item, item.current_response ?? null, reason); }}
      onRetry={() => { if (sources.length === 0) window.location.reload(); else setRetryCount((current) => current + 1); }}
      onExport={sources.find((source) => source.source_id === selectedSourceId)?.supports_export ? () => { void download(); } : undefined}
    />
  );
}
